<template>
  <div class="responsive-data-table">
    <div
      v-if="hasError"
      class="responsive-data-table__state responsive-data-table__state--error"
      role="alert"
    >
      {{ errorText }}
    </div>
    <v-data-table
      v-else-if="!isMobile"
      v-bind="desktopTableBindings"
      v-on="$listeners"
    >
      <template
        v-for="slotName in desktopSlotNames"
        v-slot:[slotName]="slotProps"
      >
        <slot :name="slotName" v-bind="slotProps" />
      </template>
    </v-data-table>

    <div v-else class="responsive-data-table__mobile">
      <div v-if="isLoading" class="responsive-data-table__state">
        {{ loadingText }}
      </div>
      <div v-else-if="!items.length" class="responsive-data-table__state">
        {{ noDataText }}
      </div>
      <div v-else class="responsive-data-table__cards">
        <article
          v-for="item in items"
          :key="itemIdentifier(item)"
          class="responsive-data-table__card"
        >
          <div class="responsive-data-table__card-header">
            <button
              type="button"
              class="responsive-data-table__card-summary"
              :aria-label="toggleLabel(item)"
              :aria-expanded="isSelected(item).toString()"
              :aria-controls="detailId(item)"
              @click="toggle(item)"
            >
              <span class="responsive-data-table__summary-content">
                <span
                  v-for="field in summaryFields"
                  :key="field.key"
                  class="responsive-data-table__summary-field"
                  :class="{ 'responsive-data-table__summary-field--primary': field.primary }"
                >
                  <slot
                    v-if="field.slot"
                    :name="`mobile-summary-${field.slot}`"
                    :item="item"
                    :field="field"
                    :value="fieldValue(field, item)"
                  >
                    <span>{{ fieldValue(field, item) }}</span>
                  </slot>
                  <template v-else>
                    <span v-if="!field.primary" class="responsive-data-table__summary-label">
                      {{ field.label }}
                    </span>
                    <span>{{ fieldValue(field, item) }}</span>
                  </template>
                </span>
              </span>
              <span class="responsive-data-table__toggle" aria-hidden="true">
                <v-icon>{{ isSelected(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </span>
            </button>
          </div>
          <div
            v-if="hasScopedSlotContent('mobile-summary-actions', { item })"
            class="responsive-data-table__summary-actions"
          >
            <slot name="mobile-summary-actions" :item="item" />
          </div>

          <div
            v-if="isSelected(item)"
            :id="detailId(item)"
            class="responsive-data-table__card-details"
          >
            <dl class="responsive-data-table__detail-list mb-0">
              <div
                v-for="field in detailFields"
                :key="field.key"
                class="responsive-data-table__detail-field"
              >
                <dt>{{ field.label }}</dt>
                <dd>
                  <slot
                    v-if="field.slot"
                    :name="`mobile-detail-${field.slot}`"
                    :item="item"
                    :field="field"
                    :value="fieldValue(field, item)"
                  >
                    {{ fieldValue(field, item) }}
                  </slot>
                  <template v-else>{{ fieldValue(field, item) }}</template>
                </dd>
              </div>
            </dl>
            <div
              v-if="hasScopedSlotContent('mobile-actions', { item })"
              class="responsive-data-table__actions"
            >
              <slot name="mobile-actions" :item="item" />
            </div>
          </div>
        </article>
      </div>

      <v-data-footer
        v-if="showFooter"
        v-bind="mobileFooterProps"
        :options="tableOptions"
        :pagination="mobilePagination"
        @update:options="$emit('update:options', $event)"
      >
        <template
          v-for="slotName in mobileFooterSlotNames"
          v-slot:[slotName]="slotProps"
        >
          <slot :name="`footer.${slotName}`" v-bind="slotProps" />
        </template>
      </v-data-footer>
    </div>
  </div>
</template>

<script>
let componentSequence = 0;

export default {
  name: "ResponsiveDataTable",
  inheritAttrs: false,
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    itemKey: {
      type: String,
      default: "id"
    },
    mobileBreakpoint: {
      type: Number,
      default: 768
    },
    mobileSummaryFields: {
      type: Array,
      default: () => []
    },
    mobileDetailFields: {
      type: Array,
      default: () => []
    },
    mobileToggleLabel: {
      type: String,
      default: "Details"
    },
    error: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      default: "Unable to load data."
    }
  },
  data() {
    componentSequence += 1;

    return {
      selectedItemKey: null,
      instanceId: `responsive-data-table-${componentSequence}`
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < this.mobileBreakpoint;
    },
    desktopTableBindings() {
      return {
        ...this.$attrs,
        headers: this.headers,
        items: this.items,
        itemKey: this.itemKey
      };
    },
    desktopSlotNames() {
      return Object.keys(this.$scopedSlots)
        .filter(slotName => !slotName.startsWith("mobile-"));
    },
    mobileFooterSlotNames() {
      return Object.keys(this.$scopedSlots)
        .filter(slotName => slotName.startsWith("footer."))
        .map(slotName => slotName.replace("footer.", ""));
    },
    summaryFields() {
      const fields = this.mobileSummaryFields.length
        ? this.mobileSummaryFields
        : this.defaultFields().slice(0, 3);

      return fields.map((field, index) => ({
        ...field,
        primary: field.primary === undefined ? index === 0 : field.primary
      }));
    },
    detailFields() {
      const summaryKeys = this.summaryFields.map(field => field.key);
      const fields = this.mobileDetailFields.length
        ? this.mobileDetailFields
        : this.defaultFields().filter(field => !summaryKeys.includes(field.key));

      return fields.filter(
        field => !summaryKeys.includes(field.key) || field.allowInDetails
      );
    },
    isLoading() {
      return Boolean(this.$attrs.loading);
    },
    hasError() {
      return this.error;
    },
    loadingText() {
      return this.$attrs.loadingText || this.$attrs["loading-text"] || "Loading...";
    },
    noDataText() {
      return this.$attrs.noDataText || this.$attrs["no-data-text"] || "No data available.";
    },
    tableOptions() {
      return this.$attrs.options || {};
    },
    totalItems() {
      const value = this.attributeValue("serverItemsLength");

      return value === undefined || value === null ? this.items.length : value;
    },
    mobilePagination() {
      const itemsPerPage = this.tableOptions.itemsPerPage || this.items.length || 1;
      const page = this.tableOptions.page || 1;
      const itemsLength = Number(this.totalItems) || 0;
      const pageStart = (page - 1) * itemsPerPage;

      return {
        page,
        pageCount: Math.max(1, Math.ceil(itemsLength / itemsPerPage)),
        itemsLength,
        itemsPerPage,
        pageStart,
        pageStop: page * itemsPerPage
      };
    },
    mobileFooterProps() {
      return this.attributeValue("footerProps") || {};
    },
    showFooter() {
      return !this.attributeValue("hideDefaultFooter");
    }
  },
  watch: {
    items() {
      this.selectedItemKey = null;
    },
    isMobile() {
      this.selectedItemKey = null;
    },
    error() {
      this.selectedItemKey = null;
    }
  },
  mounted() {
    // v-data-table emits its initial options on desktop. Mirror that behavior
    // for the mobile card renderer so server-side tables load their first page.
    if (this.isMobile) {
      this.$emit("update:options", this.tableOptions);
    }
  },
  methods: {
    defaultFields() {
      return this.headers
        .filter(header => header.value && header.value !== "actions")
        .map(header => ({
          key: header.value,
          label: header.text || header.value
        }));
    },
    itemIdentifier(item) {
      return this.valueByPath(item, this.itemKey);
    },
    detailId(item) {
      return `${this.instanceId}-details-${this.itemIdentifier(item)}`;
    },
    isSelected(item) {
      return this.selectedItemKey === this.itemIdentifier(item);
    },
    toggle(item) {
      const itemKey = this.itemIdentifier(item);

      this.selectedItemKey = this.selectedItemKey === itemKey ? null : itemKey;
    },
    toggleLabel(item) {
      const primaryField = this.summaryFields.find(field => field.primary) || this.summaryFields[0];
      const primaryValue = primaryField ? this.fieldValue(primaryField, item) : "";

      return primaryValue ? `${this.mobileToggleLabel}: ${primaryValue}` : this.mobileToggleLabel;
    },
    hasScopedSlotContent(slotName, slotProps) {
      const slot = this.$scopedSlots[slotName];
      const content = slot ? slot(slotProps) : [];

      return content.some(vnode => vnode && !vnode.isComment);
    },
    fieldValue(field, item) {
      const value = typeof field.value === "function"
        ? field.value(item)
        : this.valueByPath(item, field.value || field.key);

      return value === null || value === undefined || value === "" ? "-" : value;
    },
    attributeValue(name) {
      const kebabName = name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

      return this.$attrs[name] === undefined ? this.$attrs[kebabName] : this.$attrs[name];
    },
    valueByPath(item, path) {
      return String(path || "")
        .split(".")
        .filter(Boolean)
        .reduce((value, key) => value === null || value === undefined ? undefined : value[key], item);
    }
  }
};
</script>

<style scoped>
.responsive-data-table__state {
  color: #3f4254;
  padding: 1.5rem;
}

.responsive-data-table__state--error {
  color: #f64e60;
}

.responsive-data-table__card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.42rem;
  margin-bottom: 0.75rem;
}

.responsive-data-table__card-header {
  display: block;
}

.responsive-data-table__card-summary {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  min-height: 4.25rem;
  padding: 0.875rem 1rem;
  text-align: left;
  min-width: 0;
  width: 100%;
}

.responsive-data-table__summary-actions {
  align-items: center;
  border-top: 1px solid #e4e6ef;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-start;
  padding: 0.5rem 1rem;
}

.responsive-data-table__summary-content {
  flex: 1 1 auto;
  min-width: 0;
}

.responsive-data-table__card-summary:focus-visible {
  border-radius: 0.25rem;
  outline: 2px solid #3699ff;
  outline-offset: 3px;
}

.responsive-data-table__summary-field {
  color: #3f4254;
  display: block;
  font-size: 0.85rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.responsive-data-table__summary-field--primary {
  color: #3f4254;
  font-size: 1rem;
  font-weight: 600;
}

.responsive-data-table__summary-label::after {
  content: ": ";
}

.responsive-data-table__card-details {
  border-top: 1px solid #e4e6ef;
  padding: 1rem;
}

.responsive-data-table__detail-field + .responsive-data-table__detail-field {
  border-top: 1px solid #e4e6ef;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}

.responsive-data-table__detail-field dt {
  color: #3f4254;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.responsive-data-table__detail-field dd {
  color: #3f4254;
  margin-bottom: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.responsive-data-table__toggle {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  margin-left: 0.75rem;
}

.responsive-data-table__actions {
  border-top: 1px solid #e4e6ef;
  margin-top: 1rem;
  padding-top: 1rem;
}
</style>
